import React, { useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { Company, Persona } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { useUserPreferencesRPC } from '@/hooks/useUserPreferencesRPC';
import { useToast } from '@/hooks/use-toast';
import { WorkspaceContext } from '@/hooks/useWorkspace';
import type { WorkspaceContextType } from '@/contexts/WorkspaceContextType';

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companySlug } = useParams<{ companySlug?: string }>();
  
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [currentPersona, setCurrentPersona] = useState<Persona | null>(null);
  const [isResolvingSlug, setIsResolvingSlug] = useState(false);
  
  // Use backend-persisted preferences via RPC
  const { 
    activeCompanyId, 
    activeCompany, 
    switchActiveCompany: switchActiveCompanyBackend,
    isSwitching,
    isLoading: isLoadingPreferences 
  } = useUserPreferencesRPC();

  // Fetch all companies for the user
  const { data: companies = [], isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to fetch companies', error);
        throw error;
      }

      // Add slug to each company if not present
      return (data || []).map(company => ({
        ...company,
        slug: company.slug || company.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      }));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch personas for the current company
  const { data: companyPersonas = [], isLoading: isLoadingPersonas } = useQuery({
    queryKey: ['companyPersonas', currentCompany?.id],
    queryFn: async () => {
      if (!currentCompany?.id) return [];
      
      const { data, error } = await supabase
        .from('buyer_personas')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('journey_stage', { ascending: true });

      if (error) {
        logger.error('Failed to fetch company personas', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!currentCompany?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Resolve company slug to company data
  const resolveCompanySlug = useCallback(async (slug: string) => {
    try {
      setIsResolvingSlug(true);
      
      const { data, error } = await supabase.functions.invoke('company-resolver', {
        body: null,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
        query: { slug }
      });

      if (error) throw error;
      
      // If it was a redirect, update the URL
      if (data.isRedirect) {
        const currentPath = window.location.pathname;
        const pathParts = currentPath.split('/');
        pathParts[2] = data.slug; // Update the company slug part
        navigate(pathParts.join('/'), { replace: true });
      }
      
      return data;
    } catch (error) {
      logger.error('Failed to resolve company slug', error);
      return null;
    } finally {
      setIsResolvingSlug(false);
    }
  }, [navigate]);

  // Determine if user has multiple companies
  const hasMultipleCompanies = companies.length > 1;

  // Sync current company with backend active company
  useEffect(() => {
    if (isLoadingCompanies || isLoadingPreferences || companies.length === 0) return;

    // If we have an active company from backend, use it
    if (activeCompanyId) {
      const company = companies.find(c => c.id === activeCompanyId);
      if (company && company.id !== currentCompany?.id) {
        setCurrentCompany(company);
        setCurrentPersona(null);
      }
    } else if (companies.length > 0 && !currentCompany) {
      // No active company set, use the first one and set it as active
      const firstCompany = companies[0];
      setCurrentCompany(firstCompany);
      switchActiveCompanyBackend(firstCompany.id);
    }
  }, [companies, activeCompanyId, isLoadingCompanies, isLoadingPreferences, currentCompany, switchActiveCompanyBackend]);

  // Handle URL-based company navigation
  useEffect(() => {
    if (!companySlug || isLoadingCompanies || isResolvingSlug) return;

    const handleUrlCompany = async () => {
      // Check if URL company matches current active company
      if (currentCompany?.slug === companySlug) return;

      // Find company by slug
      let targetCompany = companies.find(c => c.slug === companySlug);
      
      // If not found, it might be an old slug - resolve it
      if (!targetCompany) {
        const resolved = await resolveCompanySlug(companySlug);
        if (resolved) {
          targetCompany = companies.find(c => c.id === resolved.id) || null;
        }
      }

      // If we found a different company, switch to it
      if (targetCompany && targetCompany.id !== currentCompany?.id) {
        switchActiveCompanyBackend(targetCompany.id);
      }
    };

    handleUrlCompany();
  }, [companySlug, companies, currentCompany, isLoadingCompanies, isResolvingSlug, resolveCompanySlug, switchActiveCompanyBackend]);

  // Restore last selected persona for the company
  useEffect(() => {
    if (!currentCompany || companyPersonas.length === 0) return;

    const lastPersonaId = localStorage.getItem(`lastPersona_${currentCompany.id}`);
    if (lastPersonaId) {
      const persona = companyPersonas.find(p => p.id === lastPersonaId);
      if (persona) {
        setCurrentPersona(persona);
      }
    }
  }, [currentCompany, companyPersonas]);

  // Action to switch company
  const switchCompany = useCallback((companyId: string) => {
    logger.info('[WorkspaceContext] switchCompany called with:', companyId);
    const company = companies.find(c => c.id === companyId);
    
    if (company && company.id !== currentCompany?.id) {
      logger.info('[WorkspaceContext] Switching from', currentCompany?.name, 'to', company.name);
      
      // Update backend state
      switchActiveCompanyBackend(companyId, {
        onSuccess: () => {
          logger.info('[WorkspaceContext] Switch successful, navigating...');
          
          // Navigate to new company workspace
          const currentPath = window.location.pathname;
          const pathParts = currentPath.split('/');
          
          // If we're in a workspace route, update the company slug
          if (pathParts[1] === 'w' && pathParts[2]) {
            pathParts[2] = company.slug!;
            navigate(pathParts.join('/'), { replace: true });
          } else {
            // Otherwise, navigate to company dashboard
            navigate(`/w/${company.slug}/dashboard`);
          }
          
          toast({
            title: "Company Switched",
            description: `Now working with ${company.name}`,
          });
        },
        onError: (error) => {
          logger.error('[WorkspaceContext] Switch failed:', error);
          toast({
            title: "Error",
            description: "Failed to switch company. Please try again.",
            variant: "destructive",
          });
        }
      });
    } else {
      logger.warn('[WorkspaceContext] Company not found or same company selected');
    }
  }, [companies, navigate, currentCompany, switchActiveCompanyBackend, toast]);

  // Action to set company
  const setCompany = useCallback((company: Company | null) => {
    setCurrentCompany(company);
    if (company) {
      localStorage.setItem('lastCompanyId', company.id);
    }
  }, []);

  // Action to set persona
  const setPersona = useCallback((persona: Persona | null) => {
    setCurrentPersona(persona);
    if (persona && currentCompany) {
      localStorage.setItem(`lastPersona_${currentCompany.id}`, persona.id);
    }
  }, [currentCompany]);

  // Determine if the workspace is ready
  const isReady = !isLoadingCompanies && !isLoadingPreferences && !isResolvingSlug && companies.length > 0 && currentCompany !== null;

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<WorkspaceContextType>(() => ({
    currentCompany,
    currentPersona,
    companies,
    companyPersonas,
    isLoadingCompanies,
    isLoadingPersonas,
    setCompany,
    setPersona,
    switchCompany,
    hasMultipleCompanies,
    isReady,
  }), [
    currentCompany,
    currentPersona,
    companies,
    companyPersonas,
    isLoadingCompanies,
    isLoadingPersonas,
    setCompany,
    setPersona,
    switchCompany,
    hasMultipleCompanies,
    isReady,
  ]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};